import { useCallback, useEffect, useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
  type AccessGrant,
  type AdminDiagnosticsOverview,
  type AdminDiagnosticsProvider,
  getAccessOverview,
  getAdminDiagnostics,
  grantAdminAccess,
  grantUserAccess,
  revokeAdminAccess,
  revokeUserAccess,
  type UserRole,
} from '../api/auth';
import { useKeyboardSafeModal } from '../hooks/useKeyboardSafeModal';
import { getProviderDisplayName } from '../lib/providerRouting';
import { ConfirmDialog } from './ConfirmDialog';

interface AdminPanelModalProps {
  open: boolean;
  role: UserRole;
  onClose: () => void;
}

interface PendingRemoval {
  email: string;
  scope: 'admin' | 'user';
}

type AdminPanelTab = 'users' | 'diagnostics';

const ROLE_LABEL: Record<UserRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  user: 'User',
};

const ADMIN_PANEL_TABS: ReadonlyArray<{ key: AdminPanelTab; label: string }> = [
  { key: 'users', label: 'User Management' },
  { key: 'diagnostics', label: 'Diagnostics' },
];

function formatAddedAt(value: string | null): string {
  if (!value) {
    return 'Bootstrap access';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) {
    return 'Never';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
}

function formatRelativeAge(value: string | null | undefined): string {
  if (!value) {
    return 'No activity yet';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  const differenceMs = Date.now() - parsed.getTime();
  const differenceSeconds = Math.max(0, Math.floor(differenceMs / 1000));
  if (differenceSeconds < 60) {
    return `${differenceSeconds}s ago`;
  }
  const differenceMinutes = Math.floor(differenceSeconds / 60);
  if (differenceMinutes < 60) {
    return `${differenceMinutes}m ago`;
  }
  const differenceHours = Math.floor(differenceMinutes / 60);
  if (differenceHours < 24) {
    return `${differenceHours}h ago`;
  }
  const differenceDays = Math.floor(differenceHours / 24);
  return `${differenceDays}d ago`;
}

function AccessGrantCard({
  grant,
  actionLabel,
  isPending,
  onRemove,
}: {
  grant: AccessGrant;
  actionLabel?: string;
  isPending: boolean;
  onRemove?: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-border-subtle bg-surface-base/65 px-3 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-slate-100">{grant.email}</p>
        <p className="mt-1 text-xs text-slate-400">
          {grant.source === 'env' ? 'Owner from environment' : `Added ${formatAddedAt(grant.addedAt)}`}
        </p>
      </div>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          disabled={isPending}
          className="shrink-0 rounded-md border border-rose-400/45 bg-rose-500/10 px-3 py-1.5 text-xs font-semibold text-rose-100 transition-colors hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isPending ? `${actionLabel}...` : actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function DiagnosticsStatusPill({
  label,
  tone,
}: {
  label: string;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
}) {
  const className =
    tone === 'success'
      ? 'border border-emerald-400/40 bg-emerald-500/10 text-emerald-100'
      : tone === 'warning'
      ? 'border border-amber-400/45 bg-amber-500/10 text-amber-100'
      : tone === 'danger'
      ? 'border border-rose-400/45 bg-rose-500/10 text-rose-100'
      : 'border border-border-subtle bg-surface-panel/70 text-slate-300';

  return (
    <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${className}`}>
      {label}
    </span>
  );
}

function DiagnosticsStat({ label, value, helper }: { label: string; value: string; helper?: string }) {
  return (
    <div className="rounded-xl border border-border-subtle bg-surface-base/55 px-3 py-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="mt-2 text-sm font-semibold text-slate-100">{value}</div>
      {helper ? <div className="mt-1 text-xs text-slate-400">{helper}</div> : null}
    </div>
  );
}

function getProviderHealth(provider: AdminDiagnosticsProvider): {
  label: string;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
} {
  if (!provider.configured) {
    return { label: 'Disabled', tone: 'neutral' };
  }
  if (provider.stream.mode === 'stream') {
    if (provider.transport && !provider.transport.connected) {
      return { label: 'Degraded', tone: 'warning' };
    }
    if (provider.stream.connected) {
      return { label: 'Connected', tone: 'success' };
    }
    if (provider.transport?.connected) {
      return { label: 'Ready', tone: 'success' };
    }
    if (provider.stream.streamCount > 0 || provider.stream.subscriberCount > 0) {
      return { label: 'Degraded', tone: 'warning' };
    }
    return { label: 'Idle', tone: 'warning' };
  }
  if (provider.catalogRefresh.lastError.length > 0) {
    return { label: 'Retrying', tone: 'warning' };
  }
  if (provider.symbolCatalogCount > 0 || provider.candleCacheCount > 0) {
    return { label: 'Ready', tone: 'success' };
  }
  return { label: 'Waiting', tone: 'neutral' };
}

function DiagnosticsProviderCard({ provider }: { provider: AdminDiagnosticsProvider }) {
  const health = getProviderHealth(provider);
  const transportTypeLabel = provider.transport
    ? provider.transport.kind === 'websocket'
      ? 'WebSocket'
      : 'HTTP stream'
    : provider.connectionKind === 'stream'
      ? 'Transport'
      : 'N/A';
  const transportLabel = provider.transport
    ? provider.transport.connected
      ? 'Reachable'
      : 'Unavailable'
    : provider.connectionKind === 'stream'
      ? 'Managed by stream hub'
      : 'N/A';
  const transportHelper = provider.transport
    ? provider.transport.connected
      ? provider.transport.latencyMs !== null
        ? `${provider.transport.latencyMs.toFixed(0)} ms - checked ${formatRelativeAge(provider.transport.checkedAt)}`
        : `Checked ${formatRelativeAge(provider.transport.checkedAt)}`
      : provider.transport.lastError.length > 0
        ? provider.transport.lastError
        : `Checked ${formatRelativeAge(provider.transport.checkedAt)}`
    : provider.connectionKind === 'stream'
      ? 'No direct probe'
      : 'No streaming transport';
  const streamLabel = provider.connectionKind === 'stream'
    ? provider.stream.connected
      ? 'Streaming live'
      : provider.stream.streamCount > 0
      ? 'Watching without live ticks'
      : 'No active streams'
    : 'REST-only provider';

  return (
    <section className="rounded-2xl border border-border-subtle bg-surface-base/55 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-slate-100">{provider.displayName}</h3>
            <DiagnosticsStatusPill label={health.label} tone={health.tone} />
            <DiagnosticsStatusPill
              label={provider.marketStatus === 'open' ? 'Market Open' : 'Market Closed'}
              tone={provider.marketStatus === 'open' ? 'success' : 'neutral'}
            />
          </div>
          <p className="mt-2 text-sm text-slate-400">
            {streamLabel} - session `{provider.sessionState}` - latest candle {formatRelativeAge(provider.latestCandleAt)}.
          </p>
        </div>
        <div className="text-right text-xs text-slate-500">
          <div>{provider.connectionKind === 'stream' ? 'Stream provider' : 'REST provider'}</div>
          <div className="mt-1">Catalog status: {provider.catalogRefresh.status}</div>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <DiagnosticsStat
          label="Candle Cache"
          value={provider.candleCacheCount.toLocaleString()}
          helper={`${provider.cachedSymbolCount} symbols across ${provider.cachedIntervalCount} intervals`}
        />
        <DiagnosticsStat
          label="Catalog Entries"
          value={provider.symbolCatalogCount.toLocaleString()}
          helper={`Last success ${formatRelativeAge(provider.catalogRefresh.lastSuccessfulRefreshAt)}`}
        />
        <DiagnosticsStat
          label="Subscribers"
          value={provider.stream.subscriberCount.toLocaleString()}
          helper={provider.connectionKind === 'stream' ? `${provider.stream.streamCount} live streams` : 'No websocket stream'}
        />
        <DiagnosticsStat
          label={transportTypeLabel}
          value={transportLabel}
          helper={transportHelper}
        />
        <DiagnosticsStat
          label="Latest Tick"
          value={provider.connectionKind === 'stream' ? formatRelativeAge(provider.stream.latestTickAt) : 'N/A'}
          helper={provider.stream.latestOpenInterest !== null ? `Open interest ${provider.stream.latestOpenInterest.toLocaleString()}` : undefined}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-border-subtle bg-surface-panel/55 px-3 py-3 text-sm text-slate-300">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Refresh State</div>
          <div className="mt-2 grid gap-2 text-xs text-slate-400 sm:grid-cols-2">
            <div>Last attempted: <span className="text-slate-200">{formatTimestamp(provider.catalogRefresh.lastAttemptedRefreshAt)}</span></div>
            <div>Next retry: <span className="text-slate-200">{formatTimestamp(provider.catalogRefresh.nextRetryAt)}</span></div>
            <div>Latest candle: <span className="text-slate-200">{formatTimestamp(provider.latestCandleAt)}</span></div>
            <div>Configured: <span className="text-slate-200">{provider.configured ? 'Yes' : 'No'}</span></div>
          </div>
          {provider.catalogRefresh.lastError.length > 0 ? (
            <div className="mt-3 rounded-lg border border-amber-400/35 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
              {provider.catalogRefresh.lastError}
            </div>
          ) : null}
        </div>

        <div className="rounded-xl border border-border-subtle bg-surface-panel/55 px-3 py-3 text-sm text-slate-300">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Connection Notes</div>
          <div className="mt-2 space-y-2 text-xs text-slate-400">
            <div>Provider key: <span className="text-slate-200">{provider.configured ? 'Configured' : 'Not configured'}</span></div>
            <div>Connection mode: <span className="text-slate-200">{provider.connectionKind}</span></div>
            {provider.transport ? (
              <div>
                {provider.transport.kind === 'websocket' ? 'WebSocket transport' : 'HTTP stream transport'}:{' '}
                <span className="text-slate-200">{provider.transport.connected ? 'Reachable' : 'Unavailable'}</span>
                {provider.transport.latencyMs !== null ? ` (${provider.transport.latencyMs.toFixed(0)} ms)` : ''}
              </div>
            ) : null}
            {provider.transport?.target ? (
              <div>Probe target: <span className="text-slate-200">{provider.transport.target}</span></div>
            ) : null}
            {provider.transport ? (
              <div>Last transport check: <span className="text-slate-200">{formatTimestamp(provider.transport.checkedAt)}</span></div>
            ) : null}
            {provider.transport && provider.transport.lastError.length > 0 ? (
              <div>Transport error: <span className="text-slate-200">{provider.transport.lastError}</span></div>
            ) : null}
            {provider.rateLimit ? (
              <div>
                BitMEX rate limit: <span className="text-slate-200">{provider.rateLimit.isBlocked ? 'Blocked' : 'Available'}</span>
                {provider.rateLimit.remaining !== null ? ` (${provider.rateLimit.remaining} remaining)` : ''}
              </div>
            ) : null}
            {provider.rateLimit?.blockedUntilAt ? (
              <div>Blocked until: <span className="text-slate-200">{formatTimestamp(provider.rateLimit.blockedUntilAt)}</span></div>
            ) : null}
            {provider.rateLimit?.resetAt ? (
              <div>Reset at: <span className="text-slate-200">{formatTimestamp(provider.rateLimit.resetAt)}</span></div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

export function AdminPanelModal({ open, role, onClose }: AdminPanelModalProps) {
  const [activeTab, setActiveTab] = useState<AdminPanelTab>('users');
  const [owners, setOwners] = useState<AccessGrant[]>([]);
  const [admins, setAdmins] = useState<AccessGrant[]>([]);
  const [users, setUsers] = useState<AccessGrant[]>([]);
  const [diagnostics, setDiagnostics] = useState<AdminDiagnosticsOverview | null>(null);
  const [adminEmailInput, setAdminEmailInput] = useState('');
  const [userEmailInput, setUserEmailInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingCreateScope, setPendingCreateScope] = useState<'admin' | 'user' | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const { dialogAutoFocus, dialogRef, isKeyboardSafeLayout, viewportStyle } = useKeyboardSafeModal(open);

  const canManageAdmins = role === 'owner';
  const canManageUsers = role === 'owner' || role === 'admin';

  const refreshAccessOverview = useCallback(async () => {
    const overview = await getAccessOverview();
    setOwners(overview.owners);
    setAdmins(overview.admins);
    setUsers(overview.users);
    setErrorMessage(null);
  }, []);

  const refreshDiagnosticsOverview = useCallback(async () => {
    const nextDiagnostics = await getAdminDiagnostics();
    setDiagnostics(nextDiagnostics);
    setErrorMessage(null);
  }, []);

  const focusTab = useCallback((tab: AdminPanelTab) => {
    window.requestAnimationFrame(() => {
      const element = document.getElementById(`admin-panel-tab-${tab}`);
      if (element instanceof HTMLButtonElement) {
        element.focus();
      }
    });
  }, []);

  const handleTabKeyDown = useCallback((event: ReactKeyboardEvent<HTMLButtonElement>, tab: AdminPanelTab) => {
    const currentIndex = ADMIN_PANEL_TABS.findIndex((item) => item.key === tab);
    if (currentIndex === -1) {
      return;
    }

    let nextTab: AdminPanelTab | null = null;
    if (event.key === 'ArrowRight') {
      nextTab = ADMIN_PANEL_TABS[(currentIndex + 1) % ADMIN_PANEL_TABS.length].key;
    } else if (event.key === 'ArrowLeft') {
      nextTab = ADMIN_PANEL_TABS[(currentIndex - 1 + ADMIN_PANEL_TABS.length) % ADMIN_PANEL_TABS.length].key;
    } else if (event.key === 'Home') {
      nextTab = ADMIN_PANEL_TABS[0].key;
    } else if (event.key === 'End') {
      nextTab = ADMIN_PANEL_TABS[ADMIN_PANEL_TABS.length - 1].key;
    }

    if (nextTab === null) {
      return;
    }

    event.preventDefault();
    setActiveTab(nextTab);
    focusTab(nextTab);
  }, [focusTab]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, open]);

  useEffect(() => {
    if (!open) {
      setActiveTab('users');
      setOwners([]);
      setAdmins([]);
      setUsers([]);
      setDiagnostics(null);
      setAdminEmailInput('');
      setUserEmailInput('');
      setIsLoading(false);
      setIsRefreshing(false);
      setPendingCreateScope(null);
      setPendingRemoval(null);
      setErrorMessage(null);
      setStatusMessage(null);
      return;
    }

    if (!canManageUsers) {
      setErrorMessage('Admin access is required to view this panel.');
      return;
    }

    let isActive = true;
    setIsLoading(true);
    setErrorMessage(null);
    setStatusMessage(null);

    void Promise.all([refreshAccessOverview(), refreshDiagnosticsOverview()])
      .catch((error: unknown) => {
        if (!isActive) {
          return;
        }
        setOwners([]);
        setAdmins([]);
        setUsers([]);
        setDiagnostics(null);
        setErrorMessage(error instanceof Error ? error.message : 'Failed to load admin panel data.');
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [canManageUsers, open, refreshAccessOverview, refreshDiagnosticsOverview]);

  const adminSectionDescription = useMemo(() => {
    if (canManageAdmins) {
      return 'Owners can grant and revoke database-backed admins.';
    }
    return 'Admins are visible here. Only owners can change this list.';
  }, [canManageAdmins]);

  const diagnosticsSummary = diagnostics?.cache ?? null;
  const diagnosticsGeneratedAt = diagnostics ? formatTimestamp(diagnostics.generatedAt) : 'Never';

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      if (activeTab === 'diagnostics') {
        await refreshDiagnosticsOverview();
        setStatusMessage('Diagnostics refreshed.');
      } else {
        await refreshAccessOverview();
        setStatusMessage('Access lists refreshed.');
      }
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to refresh admin panel data.');
    } finally {
      setIsRefreshing(false);
    }
  }, [activeTab, refreshAccessOverview, refreshDiagnosticsOverview]);

  const handleGrantAdmin = useCallback(async () => {
    if (adminEmailInput.trim().length === 0) {
      setErrorMessage('Enter an email address to grant admin access.');
      return;
    }

    setPendingCreateScope('admin');
    try {
      await grantAdminAccess(adminEmailInput.trim());
      await refreshAccessOverview();
      setAdminEmailInput('');
      setStatusMessage('Admin access updated.');
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to grant admin access.');
    } finally {
      setPendingCreateScope(null);
    }
  }, [adminEmailInput, refreshAccessOverview]);

  const handleGrantUser = useCallback(async () => {
    if (userEmailInput.trim().length === 0) {
      setErrorMessage('Enter an email address to allow a user.');
      return;
    }

    setPendingCreateScope('user');
    try {
      await grantUserAccess(userEmailInput.trim());
      await refreshAccessOverview();
      setUserEmailInput('');
      setStatusMessage('Allowed users updated.');
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to grant user access.');
    } finally {
      setPendingCreateScope(null);
    }
  }, [refreshAccessOverview, userEmailInput]);

  const handleConfirmRemoval = useCallback(async () => {
    if (!pendingRemoval) {
      return;
    }

    try {
      if (pendingRemoval.scope === 'admin') {
        await revokeAdminAccess(pendingRemoval.email);
      } else {
        await revokeUserAccess(pendingRemoval.email);
      }
      await refreshAccessOverview();
      setStatusMessage(
        pendingRemoval.scope === 'admin' ? 'Admin removed.' : 'Allowed user removed.',
      );
      setErrorMessage(null);
      setPendingRemoval(null);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update access settings.');
    }
  }, [pendingRemoval, refreshAccessOverview]);

  if (!open) {
    return null;
  }

  return (
    <>
      <div
        className={`fixed inset-0 z-50 flex bg-slate-950/65 backdrop-blur-sm ${
          isKeyboardSafeLayout ? 'items-start justify-stretch p-3 sm:p-4' : 'items-center justify-center p-4'
        }`}
        style={viewportStyle}
        onClick={onClose}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="Admin panel"
          tabIndex={-1}
          className={`w-full border border-border-subtle bg-surface-elevated shadow-[0_24px_54px_rgba(2,8,24,0.62)] ring-1 ring-slate-100/5 ${
            isKeyboardSafeLayout
              ? 'flex max-h-full min-h-0 flex-1 flex-col overflow-hidden rounded-2xl'
              : 'max-w-5xl rounded-2xl'
          }`}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-border-subtle px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-100">Admin panel</h2>
                <p className="mt-1 text-xs uppercase tracking-[0.14em] text-slate-400">{ROLE_LABEL[role]} access</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void handleRefresh()}
                  disabled={isLoading || isRefreshing || !canManageUsers}
                  className="rounded-md border border-border-subtle bg-surface-panel/65 px-3 py-1.5 text-xs font-semibold text-slate-100 transition-colors hover:border-brand-primary/45 hover:bg-surface-panel disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded p-1 text-slate-400 transition-colors hover:bg-surface-panel/70 hover:text-slate-100"
                  aria-label="Close admin panel"
                >
                  X
                </button>
              </div>
            </div>

            <div role="tablist" aria-label="Admin panel sections" className="mt-4 flex items-center gap-2">
              {ADMIN_PANEL_TABS.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    id={`admin-panel-tab-${tab.key}`}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`admin-panel-section-${tab.key}`}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => setActiveTab(tab.key)}
                    onKeyDown={(event) => handleTabKeyDown(event, tab.key)}
                    className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${
                      isActive
                        ? 'border-brand-accent/70 bg-brand-primary text-slate-950 shadow-[0_10px_26px_rgba(44,98,143,0.42)]'
                        : 'border-border-subtle bg-surface-panel/65 text-slate-200 hover:border-brand-primary/45 hover:bg-surface-panel'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`space-y-5 overflow-y-auto px-4 py-4 ${isKeyboardSafeLayout ? 'min-h-0 flex-1' : 'max-h-[72vh]'}`}>
            <div className="rounded-2xl border border-border-subtle bg-surface-panel/55 p-4 text-sm text-slate-200">
              Signed in role: <span className="font-semibold text-slate-50">{ROLE_LABEL[role]}</span>
            </div>

            {isLoading ? (
              <p className="rounded-lg border border-border-subtle bg-surface-base/50 px-3 py-4 text-sm text-slate-300">
                Loading admin panel data...
              </p>
            ) : null}

            {errorMessage ? (
              <p className="rounded-lg border border-rose-400/45 bg-rose-500/10 px-3 py-4 text-sm text-rose-100">
                {errorMessage}
              </p>
            ) : null}

            {statusMessage ? (
              <p className="rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-3 py-4 text-sm text-emerald-100">
                {statusMessage}
              </p>
            ) : null}

            {!isLoading && activeTab === 'users' ? (
              <div
                id="admin-panel-section-users"
                role="tabpanel"
                aria-labelledby="admin-panel-tab-users"
                className="space-y-5"
              >
                <section className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Owners</h3>
                      <p className="mt-1 text-xs text-slate-500">Owners come from `ALLOWED_LOGIN_EMAILS` and cannot be removed here.</p>
                    </div>
                    <span className="text-xs text-slate-500">{owners.length}</span>
                  </div>
                  {owners.length > 0 ? (
                    <div className="space-y-2">
                      {owners.map((grant) => (
                        <AccessGrantCard key={`owner-${grant.email}`} grant={grant} isPending={false} />
                      ))}
                    </div>
                  ) : (
                    <p className="rounded-lg border border-dashed border-border-subtle px-3 py-4 text-center text-sm text-slate-500">
                      No owner emails are configured.
                    </p>
                  )}
                </section>

                <section className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Admins</h3>
                      <p className="mt-1 text-xs text-slate-500">{adminSectionDescription}</p>
                    </div>
                    <span className="text-xs text-slate-500">{admins.length}</span>
                  </div>

                  {canManageAdmins ? (
                    <div className="flex flex-col gap-2 rounded-2xl border border-border-subtle bg-surface-base/55 p-3 sm:flex-row">
                      <input
                        type="email"
                        value={adminEmailInput}
                        onChange={(event) => setAdminEmailInput(event.target.value)}
                        placeholder="Add admin email"
                        autoFocus={dialogAutoFocus}
                        className="min-w-0 flex-1 rounded-md border border-border-subtle bg-surface-base/85 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-400 hover:border-brand-primary/45 focus:border-brand-primary/65 focus:outline-hidden"
                      />
                      <button
                        type="button"
                        onClick={() => void handleGrantAdmin()}
                        disabled={pendingCreateScope !== null}
                        className="rounded-md border border-brand-accent/55 bg-brand-primary px-4 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-brand-accent disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {pendingCreateScope === 'admin' ? 'Adding...' : 'Add admin'}
                      </button>
                    </div>
                  ) : null}

                  {admins.length > 0 ? (
                    <div className="space-y-2">
                      {admins.map((grant) => (
                        <AccessGrantCard
                          key={`admin-${grant.email}`}
                          grant={grant}
                          actionLabel="Remove"
                          isPending={pendingRemoval?.scope === 'admin' && pendingRemoval.email === grant.email}
                          onRemove={
                            canManageAdmins
                              ? () => setPendingRemoval({ email: grant.email, scope: 'admin' })
                              : undefined
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="rounded-lg border border-dashed border-border-subtle px-3 py-4 text-center text-sm text-slate-500">
                      No database-backed admins have been added.
                    </p>
                  )}
                </section>

                <section className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Allowed users</h3>
                      <p className="mt-1 text-xs text-slate-500">Owners and admins can manage database-backed user access.</p>
                    </div>
                    <span className="text-xs text-slate-500">{users.length}</span>
                  </div>

                  {canManageUsers ? (
                    <div className="flex flex-col gap-2 rounded-2xl border border-border-subtle bg-surface-base/55 p-3 sm:flex-row">
                      <input
                        type="email"
                        value={userEmailInput}
                        onChange={(event) => setUserEmailInput(event.target.value)}
                        placeholder="Add allowed user email"
                        autoFocus={!canManageAdmins ? dialogAutoFocus : false}
                        className="min-w-0 flex-1 rounded-md border border-border-subtle bg-surface-base/85 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-400 hover:border-brand-primary/45 focus:border-brand-primary/65 focus:outline-hidden"
                      />
                      <button
                        type="button"
                        onClick={() => void handleGrantUser()}
                        disabled={pendingCreateScope !== null}
                        className="rounded-md border border-brand-accent/55 bg-brand-primary px-4 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-brand-accent disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {pendingCreateScope === 'user' ? 'Adding...' : 'Add user'}
                      </button>
                    </div>
                  ) : null}

                  {users.length > 0 ? (
                    <div className="space-y-2">
                      {users.map((grant) => (
                        <AccessGrantCard
                          key={`user-${grant.email}`}
                          grant={grant}
                          actionLabel="Remove"
                          isPending={pendingRemoval?.scope === 'user' && pendingRemoval.email === grant.email}
                          onRemove={canManageUsers ? () => setPendingRemoval({ email: grant.email, scope: 'user' }) : undefined}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="rounded-lg border border-dashed border-border-subtle px-3 py-4 text-center text-sm text-slate-500">
                      No allowed users have been added.
                    </p>
                  )}
                </section>
              </div>
            ) : null}

            {!isLoading && activeTab === 'diagnostics' ? (
              <div
                id="admin-panel-section-diagnostics"
                role="tabpanel"
                aria-labelledby="admin-panel-tab-diagnostics"
                className="space-y-5"
              >
                <section className="rounded-2xl border border-border-subtle bg-surface-base/55 p-4 shadow-[0_18px_34px_rgba(2,8,24,0.26)]">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-100">Cache and provider diagnostics</div>
                      <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                        Snapshot generated {diagnosticsGeneratedAt}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <DiagnosticsStatusPill
                        label={diagnosticsSummary?.symbolCatalogWarming ? 'Catalog Warming' : 'Catalog Ready'}
                        tone={diagnosticsSummary?.symbolCatalogWarming ? 'warning' : 'success'}
                      />
                      <DiagnosticsStatusPill
                        label={`${diagnostics?.providers.length ?? 0} providers`}
                        tone="neutral"
                      />
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <DiagnosticsStat
                      label="Cache Path"
                      value={diagnosticsSummary?.path ?? 'Unavailable'}
                      helper="SQLite store used for candles and symbol catalogs"
                    />
                    <DiagnosticsStat
                      label="Cached Candles"
                      value={(diagnosticsSummary?.totalCandleCount ?? 0).toLocaleString()}
                      helper={`${diagnosticsSummary?.providersWithCachedCandles ?? 0} providers with cached candle data`}
                    />
                    <DiagnosticsStat
                      label="Catalog Entries"
                      value={(diagnosticsSummary?.totalSymbolCatalogCount ?? 0).toLocaleString()}
                      helper={`Updated ${formatRelativeAge(diagnosticsSummary?.latestSymbolCatalogUpdateAt)}`}
                    />
                    <DiagnosticsStat
                      label="Popularity Cache"
                      value={formatRelativeAge(diagnosticsSummary?.latestPopularityUpdateAt)}
                      helper="Latest popularity refresh recorded in cache"
                    />
                  </div>
                </section>

                <div className="space-y-4">
                  {(diagnostics?.providers ?? []).map((provider) => (
                    <DiagnosticsProviderCard key={provider.provider} provider={provider} />
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex justify-end border-t border-border-subtle px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border-subtle bg-surface-panel/75 px-4 py-2 text-sm font-semibold text-slate-100 transition-colors hover:border-brand-primary/45 hover:bg-surface-panel"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={pendingRemoval !== null}
        title={pendingRemoval?.scope === 'admin' ? 'Remove admin' : 'Remove allowed user'}
        message={
          pendingRemoval
            ? `Remove ${pendingRemoval.email} from the ${pendingRemoval.scope === 'admin' ? 'admin' : 'allowed user'} list?`
            : ''
        }
        onClose={() => setPendingRemoval(null)}
        onConfirm={handleConfirmRemoval}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        confirmTone="danger"
      />
    </>
  );
}
